import React, { createContext, useState, useEffect } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import type { User, UpdateUserData } from '@/types';
import { AuthService } from '@/services/auth.service';
import { UserService } from '@/services/user.service';

export interface AuthContextValue {
    firebaseUser: FirebaseUser | null;
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    login: typeof AuthService.login;
    register: typeof AuthService.register;
    logout: typeof AuthService.logout;
    sendPasswordReset: typeof AuthService.sendPasswordReset;
    updateProfile: (updates: UpdateUserData) => Promise<void>;
    refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchUserProfile = async (uid: string) => {
        try {
            const userDoc = await UserService.getUser(uid);
            setUser(userDoc);
        } catch (error) {
            console.error('Failed to fetch user profile:', error);
            setUser(null);
        }
    };

    useEffect(() => {
        const unsubscribe = AuthService.onAuthStateChanged(async (fbUser) => {
            setFirebaseUser(fbUser);

            if (fbUser) {
                await fetchUserProfile(fbUser.uid);
            } else {
                setUser(null);
            }

            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const updateProfile = async (updates: UpdateUserData) => {
        if (!user) throw new Error('No user logged in');
        await UserService.updateUser(user.id, updates);
        await fetchUserProfile(user.id); // Refresh local state
    };

    const refreshProfile = async () => {
        if (firebaseUser) {
            await fetchUserProfile(firebaseUser.uid);
        }
    };

    const value: AuthContextValue = {
        firebaseUser,
        user,
        isAuthenticated: !!firebaseUser,
        isLoading,
        isAdmin: user?.role === 'admin',
        login: AuthService.login,
        register: AuthService.register,
        logout: AuthService.logout,
        sendPasswordReset: AuthService.sendPasswordReset,
        updateProfile,
        refreshProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
